import React, { useState, useRef } from 'react';
import { db, auth } from './firebase';
import {
  collection, getDocs, doc, writeBatch, setDoc, arrayUnion, serverTimestamp,
  getCountFromServer, query, where, Timestamp, DocumentReference, GeoPoint, Bytes
} from 'firebase/firestore';

// ---- লাইভে যাওয়ার আগে টেস্ট ডেটা মুছে অ্যাপকে ০ থেকে শুরু করানোর এককালীন টুল ----
// কী মুছবে আর কী থাকবে, সেটা Sohan 2026-10-08-এ নিজে বেছে দিয়েছেন: সব হিসাব, কাস্টমার,
// বেতন/অগ্রিম/ছুটির রেকর্ড আর Recycle Bin খালি হবে; প্রোডাক্ট থাকবে কিন্তু স্টক ০ আর
// purchase history খালি হবে; কর্মচারী, লগইন/Role, ছুটির দিন, HR টাস্ক, সেটিংস থাকবে।
//
// অ্যাপের সাধারণ নিয়ম হলো কিছুই পুরোপুরি মোছা হয় না (সব Recycle Bin-এ যায়)। এটা তার
// ইচ্ছাকৃত ব্যতিক্রম -- শত শত টেস্ট রেকর্ড Recycle Bin-এ ঢেলে দেওয়ার বদলে মোছার আগে
// পুরো ডেটার একটা JSON ব্যাকআপ ফাইল ডাউনলোড হয় (Sohan এটাই বেছে নিয়েছেন)। দুই ধাপে
// কাজ করে: ধাপ ১ ব্যাকআপ নেয়, ধাপ ২ ঠিক ব্যাকআপে থাকা ডকুমেন্টগুলোই মোছে -- ফলে মাঝখানে
// কেউ নতুন সেল করলে সেটা ব্যাকআপ ছাড়া মুছে যায় না।
const CLEARED = [
  { name: 'sales', label: 'Sales (incl. website & courier orders)' },
  { name: 'purchases', label: 'Purchases' },
  { name: 'returns', label: 'Returns' },
  { name: 'damaged', label: 'Damaged products' },
  { name: 'duePayments', label: 'Due payments received' },
  { name: 'transactions', label: 'Accounts income / expense entries' },
  { name: 'productStats', label: 'Sales statistics' },
  { name: 'courierLedger', label: 'Courier payment ledger' },
  { name: 'customers', label: 'Customers' },
  { name: 'payrollTransactions', label: 'Salary & advance records' },
  { name: 'leaveRecords', label: 'Leave records' },
  { name: 'recycleBin', label: 'Recycle Bin' },
  { name: 'deleted_products', label: 'Recycle Bin (deleted products)' }
];

const CHUNK = 400; // writeBatch-এ সর্বোচ্চ ৫০০টা কাজ ধরে

// Firestore-এর বিশেষ টাইপগুলো (তারিখ ইত্যাদি) JSON-এ হারিয়ে না যায়, তাই ট্যাগ দিয়ে রাখা --
// পরে কখনো ব্যাকআপ থেকে ফেরাতে হলে এগুলো আবার আসল টাইপে বদলানো যাবে।
const toPlain = (v) => {
  if (v instanceof Timestamp) return { __type: 'timestamp', seconds: v.seconds, nanoseconds: v.nanoseconds };
  if (v instanceof DocumentReference) return { __type: 'ref', path: v.path };
  if (v instanceof GeoPoint) return { __type: 'geopoint', latitude: v.latitude, longitude: v.longitude };
  if (v instanceof Bytes) return { __type: 'bytes', base64: v.toBase64() };
  if (Array.isArray(v)) return v.map(toPlain);
  if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, toPlain(x)]));
  return v;
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const fileStamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};

const ResetData = () => {
  const [backup, setBackup] = useState(null); // { ids, productIds, webOrderIds, filename }
  const [before, setBefore] = useState(null); // { [collection]: count, productsWithStock }
  const [after, setAfter] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const lockRef = useRef(false);

  const countNow = async () => {
    const counts = {};
    for (const c of CLEARED) {
      counts[c.name] = (await getCountFromServer(collection(db, c.name))).data().count;
    }
    counts.productsWithStock = (await getCountFromServer(query(collection(db, 'products'), where('stock', '!=', 0)))).data().count;
    return counts;
  };

  const takeBackup = async () => {
    if (lockRef.current) return;
    lockRef.current = true;
    setError('');
    setDone(false);
    setAfter(null);
    try {
      const filename = `kizuna-backup-${fileStamp()}.json`;
      const out = {
        app: 'Kizuna Inventory',
        kind: 'data-reset-backup',
        version: 1,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.email || '',
        collections: {}
      };
      const ids = {};
      const counts = {};
      for (const c of CLEARED) {
        setBusy(`Reading ${c.label}...`);
        const snap = await getDocs(collection(db, c.name));
        out.collections[c.name] = snap.docs.map((d) => ({ id: d.id, data: toPlain(d.data()) }));
        ids[c.name] = snap.docs.map((d) => d.id);
        counts[c.name] = snap.size;
      }
      // প্রোডাক্ট মোছা হবে না, কিন্তু স্টক আর purchase history বদলাবে -- তাই পুরো প্রোডাক্টও ব্যাকআপে
      setBusy('Reading products...');
      const prodSnap = await getDocs(collection(db, 'products'));
      out.collections.products = prodSnap.docs.map((d) => ({ id: d.id, data: toPlain(d.data()) }));
      const productIds = prodSnap.docs
        .filter((d) => Number(d.data().stock || 0) !== 0 || (d.data().history || []).length > 0)
        .map((d) => d.id);
      counts.productsWithStock = prodSnap.docs.filter((d) => Number(d.data().stock || 0) !== 0).length;

      downloadJson(out, filename);
      setBackup({ ids, productIds, webOrderIds: ids.sales.filter((id) => id.startsWith('WEB-')), filename });
      setBefore(counts);
    } catch (err) {
      setError(`Backup failed: ${err.message}`);
    }
    setBusy('');
    lockRef.current = false;
  };

  const runReset = async () => {
    if (lockRef.current || !backup || confirmText !== 'RESET') return;
    lockRef.current = true;
    setError('');
    try {
      // ১) আগে ইমপোর্ট-করা ওয়েবসাইট অর্ডারগুলোর নম্বর আলাদা করে রাখা -- Website Orders পেজ
      // এগুলো sales-এ WEB-<নম্বর> খুঁজে চেনে, সেল মুছে গেলে এগুলো আবার "নতুন" হয়ে ফিরে আসত।
      if (backup.webOrderIds.length > 0) {
        setBusy('Remembering already-imported website orders...');
        await setDoc(doc(db, 'settings', 'dataReset'), { importedWebOrders: arrayUnion(...backup.webOrderIds) }, { merge: true });
      }

      // ২) ঠিক ব্যাকআপে থাকা ডকুমেন্টগুলোই মোছা (ব্যাকআপের পরে আসা কিছু না)
      for (const c of CLEARED) {
        const list = backup.ids[c.name];
        for (let i = 0; i < list.length; i += CHUNK) {
          setBusy(`Deleting ${c.label}... ${Math.min(i + CHUNK, list.length)} / ${list.length}`);
          const batch = writeBatch(db);
          list.slice(i, i + CHUNK).forEach((id) => batch.delete(doc(db, c.name, id)));
          await batch.commit();
        }
      }

      // ৩) প্রোডাক্টের স্টক ০ আর purchase history খালি -- শুধু যেগুলো এখনো আছে (update মুছে
      // যাওয়া ডকুমেন্টে চালালে পুরো batch ব্যর্থ হয়)
      setBusy('Setting product stock to 0...');
      const fresh = await getDocs(collection(db, 'products'));
      const stillThere = new Set(fresh.docs.map((d) => d.id));
      const toZero = backup.productIds.filter((id) => stillThere.has(id));
      for (let i = 0; i < toZero.length; i += CHUNK) {
        const batch = writeBatch(db);
        toZero.slice(i, i + CHUNK).forEach((id) => batch.update(doc(db, 'products', id), { stock: 0, history: [] }));
        await batch.commit();
      }

      await setDoc(doc(db, 'settings', 'dataReset'), {
        lastResetAt: serverTimestamp(),
        lastResetBy: auth.currentUser?.email || 'unknown',
        lastBackupFile: backup.filename
      }, { merge: true });

      setBusy('Checking that everything is now 0...');
      setAfter(await countNow());
      setBackup(null);
      setConfirmText('');
      setDone(true);
    } catch (err) {
      // ব্যাকআপ state-এ থেকে যায়, তাই আবার Reset চাপলে যেখানে থেমেছিল সেখান থেকে শেষ করবে
      setError(`Reset stopped part-way: ${err.message}. Nothing is lost -- you have the backup file. Press "Reset Now" again to finish the rest.`);
    }
    setBusy('');
    lockRef.current = false;
  };

  const checkCounts = async () => {
    if (lockRef.current) return;
    lockRef.current = true;
    setError('');
    setBusy('Counting...');
    try {
      setAfter(await countNow());
    } catch (err) {
      setError(`Couldn't count: ${err.message}`);
    }
    setBusy('');
    lockRef.current = false;
  };

  const rows = [
    ...CLEARED.map((c) => ({ key: c.name, label: c.label })),
    { key: 'productsWithStock', label: 'Products with stock (products themselves are kept)' }
  ];

  const btn = (bg, enabled) => ({
    padding: '12px 22px', borderRadius: '12px', border: 'none', fontWeight: 'bold', fontSize: '14px',
    background: enabled ? bg : '#ccc', color: '#fff', cursor: enabled ? 'pointer' : 'not-allowed'
  });

  const canReset = !!backup && confirmText === 'RESET' && !busy;

  return (
    <div style={{
      flexBasis: '100%', background: '#fff', padding: '30px', borderRadius: '25px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '2px solid #ffcdd2'
    }}>
      <h3 style={{ marginBottom: '10px', color: '#c0392b' }}>⚠️ Danger Zone — Reset Test Data</h3>
      <p style={{ color: '#555', fontSize: '14px', lineHeight: 1.6, marginBottom: '6px' }}>
        Clears every sale, purchase, return, due payment, account entry, customer, salary/advance and leave record,
        and empties the Recycle Bin, so the app starts from zero before going live.
      </p>
      <p style={{ color: '#555', fontSize: '14px', lineHeight: 1.6, marginBottom: '20px' }}>
        <b>Kept:</b> products (names, prices, categories), employees, logins and roles, holidays, HR calendar and settings.
        Product stock becomes <b>0</b> — the Sales page will show no products until real stock is entered through <b>Purchase</b>.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Step 1 — Download a backup</div>
          <button type="button" onClick={takeBackup} disabled={!!busy} style={btn('#4a00e0', !busy)}>
            ⬇️ Download Backup
          </button>
          {backup && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#218c61' }}>
              ✅ Backup saved to your Downloads folder as <b>{backup.filename}</b>. Keep this file safe — it is the only copy of what will be deleted.
            </div>
          )}
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Step 2 — Reset</div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESET to confirm"
              disabled={!backup || !!busy}
              style={{ padding: '12px 15px', borderRadius: '12px', border: '1px solid #ddd', fontSize: '14px', width: '220px' }}
            />
            <button type="button" onClick={runReset} disabled={!canReset} style={btn('#c0392b', canReset)}>
              Reset Now
            </button>
          </div>
          {!backup && !done && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>Download the backup first.</div>
          )}
        </div>

        {busy && <div style={{ fontSize: '14px', color: '#4a00e0', fontWeight: 'bold' }}>⏳ {busy}</div>}
        {error && <div style={{ fontSize: '14px', color: '#c0392b', background: '#ffebee', padding: '10px 14px', borderRadius: '10px' }}>{error}</div>}
        {done && (
          <div style={{ fontSize: '14px', color: '#218c61', background: '#eafff3', padding: '10px 14px', borderRadius: '10px' }}>
            ✅ Reset finished. Check the table below, then open Home, Accounts, Reports and Due to see them at 0.
          </div>
        )}

        {(before || after) && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#888', fontSize: '12px' }}>
                <th style={{ padding: '8px' }}>What</th>
                <th style={{ padding: '8px' }}>Before</th>
                <th style={{ padding: '8px' }}>Now</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px' }}>{r.label}</td>
                  <td style={{ padding: '8px' }}>{before ? before[r.key] : '—'}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', color: after ? (after[r.key] === 0 ? '#218c61' : '#c0392b') : '#999' }}>
                    {after ? (after[r.key] === 0 ? '0 ✅' : after[r.key]) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div>
          <button type="button" onClick={checkCounts} disabled={!!busy} style={{ ...btn('#555', !busy), padding: '8px 16px', fontSize: '13px' }}>
            🔍 Check current counts
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetData;
